import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';

interface PromptsDialogProps {
  open: boolean;
  rfpName: string;
  prompts: {
    questionExtraction?: string;
    answerGeneration?: string;
    cohesionReview?: string;
  };
  onClose: () => void;
}

export default function PromptsDialog({ open, rfpName, prompts, onClose }: PromptsDialogProps) {
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);

  const handleCopy = (promptText: string, promptType: string) => {
    navigator.clipboard.writeText(promptText);
    setCopiedPrompt(promptType);
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const hasPrompts = prompts.questionExtraction || prompts.answerGeneration || prompts.cohesionReview;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <CodeIcon color="primary" />
            <Typography variant="h6" component="span">
              Prompts Used for {rfpName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!hasPrompts ? (
          <Alert severity="info">
            Prompts are not available for this proposal. This feature was added after this proposal was created.
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Below are the exact prompts that were sent to Claude Code when generating this proposal.
              These prompts include your custom system prompt, proposal narrative, and specific instructions for each step.
            </Typography>

            {prompts.questionExtraction && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    1. Question Extraction Prompt
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      This prompt was used to extract questions from the RFP document.
                    </Typography>
                    <Box position="relative">
                      <TextField
                        fullWidth
                        multiline
                        value={prompts.questionExtraction}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            backgroundColor: 'grey.50',
                          }
                        }}
                        minRows={10}
                        maxRows={20}
                      />
                      <Tooltip title={copiedPrompt === 'extraction' ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(prompts.questionExtraction!, 'extraction')}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {prompts.answerGeneration && (
              <Accordion defaultExpanded={!prompts.questionExtraction}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    2. Answer Generation Prompt
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      This prompt was used to generate answers to the RFP questions. It includes your proposal narrative, custom instructions, and context documents.
                    </Typography>
                    <Box position="relative">
                      <TextField
                        fullWidth
                        multiline
                        value={prompts.answerGeneration}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            backgroundColor: 'grey.50',
                          }
                        }}
                        minRows={10}
                        maxRows={20}
                      />
                      <Tooltip title={copiedPrompt === 'generation' ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(prompts.answerGeneration!, 'generation')}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {prompts.cohesionReview && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    3. Cohesion Review Prompt
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      This prompt was used to review the proposal for narrative consistency and make refinements.
                    </Typography>
                    <Box position="relative">
                      <TextField
                        fullWidth
                        multiline
                        value={prompts.cohesionReview}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            backgroundColor: 'grey.50',
                          }
                        }}
                        minRows={10}
                        maxRows={20}
                      />
                      <Tooltip title={copiedPrompt === 'cohesion' ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(prompts.cohesionReview!, 'cohesion')}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
